import { useEffect, useState, useRef } from 'react';

// Hook for scroll-triggered animations
export const useScrollAnimation = (threshold = 0.2) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isVisible];
};

// Hook for parallax scroll effect
export const useParallax = (speed = 0.5) => {
  const [offset, setOffset] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const scrolled = window.innerHeight - rect.top;
        const parallaxOffset = scrolled * speed * 0.1;
        setOffset(parallaxOffset);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return [ref, offset];
};

// Parallax wrapper component
export const ParallaxSection = ({ children, className = '', speed = 0.3 }) => {
  const [ref, offset] = useParallax(speed);

  return (
    <div 
      ref={ref} 
      className={`parallax-section ${className}`}
      style={{ transform: `translateY(${offset}px)` }}
    >
      {children}
    </div>
  );
};

// Fade-in on scroll component
export const FadeInOnScroll = ({ 
  children, 
  className = '', 
  direction = 'up', 
  delay = 0,
  duration = 0.8 
}) => {
  const [ref, isVisible] = useScrollAnimation(0.15);

  const directions = {
    up: 'translate-y-10',
    down: '-translate-y-10',
    left: 'translate-x-10',
    right: '-translate-x-10',
    none: ''
  };

  return (
    <div
      ref={ref}
      className={`transition-all ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translate(0, 0)' : undefined,
        transitionDuration: `${duration}s`,
        transitionDelay: `${delay}s`
      }}
    >
      <div className={!isVisible ? directions[direction] : ''}>
        {children}
      </div>
    </div>
  );
};

// Stagger children animation
export const StaggerChildren = ({ children, className = '', staggerDelay = 0.1 }) => {
  const [ref, isVisible] = useScrollAnimation(0.1);

  return (
    <div ref={ref} className={className}>
      {Array.isArray(children) ? children.map((child, index) => (
        <div
          key={index}
          className="transition-all duration-700 ease-out"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transitionDelay: `${index * staggerDelay}s`
          }}
        >
          {child}
        </div>
      )) : children}
    </div>
  );
};

// Scale on scroll component
export const ScaleOnScroll = ({ children, className = '' }) => {
  const [ref, isVisible] = useScrollAnimation(0.2);

  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1)' : 'scale(0.95)'
      }}
    >
      {children}
    </div>
  );
};
